/* eslint-disable no-unused-vars */
/** @module Auth0PostLoginHandlerNewSsoUser */
/* eslint-disable import/extensions */
import Auth0Constants from '../../../../utils/auth0-constants.mjs';
import Auth0UtilsBrowser from '../../../../utils/auth0-utils-browser.mjs';
import Auth0PostLoginHandlerBase from '../auth0-post-login-handler-base.mst.mjs';
import Auth0PostLoginHandlerAccountLinking from './auth0-post-login-handler-account-linking.mjs';
import Auth0HandlerRegistry from '../auth0-post-login-handler-config.mjs';
import Auth0UserSessionHelper from '../../../../utils/auth0-session-helper.mjs';
import Auth0TokenStorageHelper from '../auth0-token-storage-helper.mjs';
import logger from '../../../../utils/logging/auth0-logger.mjs';

class Auth0PostLoginHandlerNewSsoUser extends Auth0PostLoginHandlerBase {
  constructor(options) {
    super(options);
    this._options = options;
    this._initLogger('Auth0PostLoginHandlerNewSsoUser');
  }

  _shouldHandleSync = (loginData) => {
    return Auth0PostLoginHandlerNewSsoUser.isIdpUserOnboardingRedirect(loginData)
      && !Auth0UserSessionHelper.isLinkedToSsAccount; // if already linked, just let the default SSHome handler field this request
  };

  /**
   * @inheritdoc
   */
  async shouldHandle(loginData) {
    return Promise.resolve(this._shouldHandleSync(loginData));
  }

  /**
   * @inheritdoc
   */
  async run(loginData) {
    // Store tokens in the default locations (legacy _lat and _latv3 cookies)
    if (loginData.tokens) {
      Auth0TokenStorageHelper.storeTokensDefault(loginData.tokens);
    }

    await this._showSpinner({ parent: 'Auth0PostLoginHandlerNewSsoUser', waitTextOverrideKey: 'please-wait-new-account', waitTextReplacementArgs: { appName: this._whiteLabelHelper.appName } });
    await this._handleCreateNewSsoUser(loginData);
  }

  _handleCreateNewSsoUser = async (userAuthData) => {
    this._setContextState(userAuthData);

    console.log(`_handleCreateNewSsoUser => unlinked user ${userAuthData?.user?.sub} is requesting new user onboarding using IDP ${this._idpContext.id}...`);
    const startTime = Date.now();
    let newUser;
    try {
      newUser = await this._auth0ManagementApi.createUser({
        source: Auth0Constants.USER_ONBOARDING_CHANNEL_SSO,
        idpUserProfile: userAuthData.user,
        ...this._idpOnboardingContext,
      });
      if (newUser.statusCode === Auth0Constants.ERROR_CODE_CREATE_USER_PARTIAL_SUCCESS) {
        logger.warn(`_handleCreateNewSsoUser => createUser partial success! ${newUser.statusMessage}`);
      }
    } catch (error) {
      logger.error(`_handleCreateNewSsoUser createUser failed => ${JSON.stringify(error)}`);
      if (error.code === Auth0Constants.ERROR_CODE_CREATE_USER_ALREADY_EXISTS) {
        // in this case, redirect to account linking instead of outright failing...
        // instantiate a SSO linking handler and hand off
        const linkingHandler = Auth0HandlerRegistry.handlerFromConfig(Auth0PostLoginHandlerAccountLinking, this._options);
        await linkingHandler.run({
          ...userAuthData,
          message: 'error-create-user-already-exists',
          messageReplacementArgs: { email: userAuthData?.user?.email || 'unknown', idpName: this._idpContext.name },
        });
        return;
      }
      if (error.code === Auth0Constants.ERROR_CODE_CREATE_USER_TIMEOUT) {
        error.key = 'error-create-user-timeout';
      } else if (error.code === Auth0Constants.ERROR_CODE_CREATE_USER_CAPTCHA_FAIL) {
        error.key = 'error-create-user-captcha';
      } else if (error.code === Auth0Constants.ERROR_CODE_CREATE_USER_FRAUD) {
        error.key = 'error-create-user-fraud';
        error.hideContactSupportMessage = true;
      } else if (error.code === Auth0Constants.ERROR_CODE_CREATE_USER_EMAIL_TOO_LONG) {
        error.key = 'error-sso-email-address-too-long';
        error.keyReplacementVars = { idpName: this._idpContext.name };
      }
      error.displayOnNotificationPage = true;
      throw error;
    }

    console.log(`Auth0PostLoginHandlerNewSsoUser => created new user ${JSON.stringify(newUser)}...`);

    // New user custom actions
    console.log(`_handleCreateNewSsoUser => run post-onboarding custom actions for IDP ${this._idpContext.id}...`);
    await this._idpContext.runNewUserOnboardingActions(newUser);

    // finally call post onboarding actions that are executed for all IdPs
    // nothing to do atm...
    // await Auth0NewUserOnboardingHelper.runNewUserOnboardingActions(newUser);

    // drop marker that we need post registration processing actions at next login (required data not available here)
    Auth0UtilsBrowser.setOption({
      cookieName: Auth0Constants.AUTH0_NEEDS_NEW_USER_ACTIONS,
      value: '1',
      expiryDay: 1,
    });

    // still logged in as IdP user and now we're linked. Refresh should let us in...
    const endTime = Date.now();
    const elapsedTime = endTime - startTime;

    // The current access token does not have the required claims from the SS user at this point and this "refresh" will correct that...
    this._refreshLoginOnAppDomain();
  };

  _refreshLoginOnAppDomain = () => {
    this._writeRefreshAuthCookie();
    console.log(`new user onboard forcing login refresh for ${this._idpContext.id}...`);
    document.location.href = this._appLoginUrl;
  };

  _writeRefreshAuthCookie = () => {
    const refreshOptions = { idpId: this._idpContext.id };
    console.log(`new user onboard _writeRefreshAuthCookie => ${JSON.stringify(refreshOptions)}`);
    Auth0UtilsBrowser.setOption({
      cookieName: Auth0Constants.AUTH0_CHANNEL_REFRESH,
      value: refreshOptions,
      stringify: true,
      expirySec: 60,
    });
  };

  _setContextState = (userAuthData) => {
    this._idpOnboardingContext = Auth0PostLoginHandlerNewSsoUser.getIdpUserOnboardingRedirectContext(userAuthData);
    if (!this._idpOnboardingContext) {
      throw new Error('Cannot complete user onboarding because required new user data was not found in app state.');
    }
    console.log(`idp onboarding detected for ${JSON.stringify(this._idpOnboardingContext)}`);

    this._idpContext = this._socialIdpHelper.getIdpById(this._idpOnboardingContext.idpId);
    console.log(`Auth0PostLoginHandlerNewSsoUser => IDP CONTEXT: ONBOARDING REQUEST DETECTED FOR ${this._idpContext.name}`);
  };

  static isIdpUserOnboardingRequest = (searchParams = null) => {
    return !!this.getIdpUserOnboardingRequestContext(searchParams);
  };

  static getIdpUserOnboardingRequestContext = (searchParams = null) => {
    searchParams = searchParams || Auth0UtilsBrowser.parseQueryString();
    const additionalState = searchParams.get(Auth0Constants.AUTH0_ADDITIONAL_APP_STATE);
    return this._isIdpUserOnboardingInternal(additionalState);
  };

  static isIdpUserOnboardingRedirect = (userAuthData) => {
    return !!this.getIdpUserOnboardingRedirectContext(userAuthData);
  };

  static getIdpUserOnboardingRedirectContext = (userAuthData) => {
    const additionalState = userAuthData.appState[Auth0Constants.AUTH0_ADDITIONAL_APP_STATE];
    return this._isIdpUserOnboardingInternal(additionalState);
  };

  static _isIdpUserOnboardingInternal = (additionalState) => {
    console.log(`_isIdpUserOnboardingInternal => additionalState is "${additionalState}"`);
    if (!additionalState) return false;

    const additionalAppState = JSON.parse(additionalState);
    if (!additionalAppState || !additionalAppState[Auth0Constants.AUTH0_ONBOARD_REQUEST_DATA_KEY]) return false;

    const idpOnboardingContext = additionalAppState[Auth0Constants.AUTH0_ONBOARD_REQUEST_DATA_KEY];
    console.log(`_isIdpUserOnboardingInternal has detected an onboard request => ${JSON.stringify(idpOnboardingContext)}`);
    return idpOnboardingContext;
  };
}

export default Auth0PostLoginHandlerNewSsoUser;
